import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    // If user used browser back/forward, restore saved position
    if (savedPosition) {
      return savedPosition
    }
    // If navigating to a hash anchor, scroll to it
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    // Otherwise scroll to top
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/register/:token',
      name: 'register-with-token',
      component: () => import('@/views/RegisterView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/join/:group_id/:token',
      name: 'join-group',
      component: () => import('@/views/InviteView.vue')
    },
    {
      path: '/invite/:token',
      name: 'invite',
      component: () => import('@/views/InviteView.vue')
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/groups/create',
      name: 'create-group',
      component: () => import('@/views/CreateGroupView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/groups/:id',
      name: 'group-detail',
      component: () => import('@/views/GroupDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/groups/:id/edit',
      name: 'edit-group',
      component: () => import('@/views/EditGroupView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/groups/:id/transactions',
      name: 'group-transactions',
      component: () => import('@/views/GroupTransactionsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/groups/:id/transactions/new',
      name: 'create-transaction',
      component: () => import('@/views/CreateTransactionView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/collab/:sessionId',
      name: 'collab-session',
      component: () => import('@/views/CreateTransactionView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/groups/:id/transactions/:transactionId',
      name: 'view-transaction',
      component: () => import('@/views/CreateTransactionView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/profile/edit',
      name: 'edit-profile',
      component: () => import('@/views/EditProfileView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/transactions',
      name: 'transactions',
      component: () => import('@/views/TransactionsView.vue'),
      meta: { requiresAuth: true }
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Initialize auth state if not already done
  if (!authStore.user && authStore.accessToken) {
    await authStore.initializeAuth()
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest)

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login if trying to access protected route
    next('/login')
  } else if (requiresGuest && authStore.isAuthenticated) {
    // Redirect to dashboard if trying to access guest-only route while authenticated
    next('/dashboard')
  } else {
    next()
  }
})

export default router